import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Loader2, CircleDot, Flag, CircleUser } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Switch } from '@/shared/components/ui/switch';
import { Label } from '@/shared/components/ui/label';
import { Field, FieldLabel } from '@/shared/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { FormFieldDatePicker } from '@/shared/components/custom/form-field';
import { useProjectMembers } from '@/features/projects/public';
import { useBoardGroups } from '@/features/boards/public';
import { useProjectStatuses } from '../../hooks/useProjectStatuses';
import { useBulkEditTasks } from '../../hooks/useTaskBulkMutations';
import { bulkEditTasksSchema, type BulkEditTasksInput } from '../../schemas';
import type { BulkEditTasksApiRequest, TaskAssignee } from '../../types';
import { AssigneePicker } from '../TaskDialog/AssigneePicker';

interface BulkEditDialogProps {
  taskIds: string[];
  boardId: string;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

// Sentinel for the "no change" option — Radix Select can't use an empty string
// as an item value, but the underlying form field's "untouched" value is ''.
const NO_CHANGE = '__no_change__';

// Shared box styling for every dropdown/date field, matching the Figma spec
// (nodes 4636:6888-4636:6926): subtle border, 8px radius, icon + value + chevron.
// Uses the app's own surface/border tokens (not the mockup's literal light-mode
// hex) so it stays correct in dark mode too.
const FIELD_BOX_CLASS =
  'w-full gap-2.5 rounded-lg border-input bg-transparent px-3.5 py-[11px] h-auto font-normal justify-start dark:bg-input/30';
const FIELD_LABEL_CLASS = 'text-xs font-medium text-muted-foreground';

export function BulkEditDialog({
  taskIds,
  boardId,
  projectId,
  open,
  onOpenChange,
  onSuccess,
}: BulkEditDialogProps) {
  const { t } = useTranslation('tasks');
  const { mutate: bulkEdit, isPending } = useBulkEditTasks(projectId);
  const { statuses } = useProjectStatuses(boardId);
  const { groups } = useBoardGroups(boardId);
  const { data: members = [] } = useProjectMembers(projectId);
  const [selectedAssignee, setSelectedAssignee] = useState<TaskAssignee | null>(null);

  const form = useForm<BulkEditTasksInput>({
    resolver: zodResolver(bulkEditTasksSchema),
    defaultValues: {
      statusName: '',
      groupName: '',
      assigneeEmail: '',
      clearAssignee: false,
      priority: '',
      dueDate: null,
      startDate: null,
    },
  });

  const clearAssignee = form.watch('clearAssignee');

  const priorityOptions = [
    { value: '1', label: t('priority.low') },
    { value: '2', label: t('priority.medium') },
    { value: '3', label: t('priority.high') },
    { value: '4', label: t('priority.urgent') },
  ];

  function handleAssigneeSelect(userId: string | null) {
    form.setValue('clearAssignee', false);
    if (!userId) {
      setSelectedAssignee(null);
      form.setValue('assigneeEmail', '');
      return;
    }
    const member = members.find((m) => m.userId === userId);
    setSelectedAssignee(
      member
        ? {
            id: member.userId,
            name: member.displayName,
            avatarUrl: member.avatarUrl ?? undefined,
            color: '',
          }
        : null,
    );
    form.setValue('assigneeEmail', member?.email ?? '');
  }

  function buildPayload(data: BulkEditTasksInput): BulkEditTasksApiRequest | null {
    const payload: BulkEditTasksApiRequest = { boardId, taskIds };
    let hasFields = false;

    if (data.statusName) {
      payload.statusName = data.statusName;
      hasFields = true;
    }
    if (data.groupName) {
      payload.groupName = data.groupName;
      hasFields = true;
    }
    if (data.clearAssignee) {
      payload.clearAssignee = true;
      hasFields = true;
    } else if (data.assigneeEmail?.trim()) {
      payload.assigneeEmail = data.assigneeEmail.trim();
      hasFields = true;
    }
    if (data.priority !== '' && data.priority !== undefined) {
      payload.priority = parseInt(data.priority, 10);
      hasFields = true;
    }
    if (data.startDate) {
      payload.startDate = { date: data.startDate.split('T')[0] };
      hasFields = true;
    }
    if (data.dueDate) {
      payload.dueDate = { date: data.dueDate.split('T')[0] };
      hasFields = true;
    }

    return hasFields ? payload : null;
  }

  function handleSubmit(data: BulkEditTasksInput) {
    form.clearErrors('root');
    const payload = buildPayload(data);
    if (!payload) {
      form.setError('root', { message: t('bulkEdit.noFieldsWarning') });
      return;
    }

    bulkEdit(payload, {
      onSuccess: () => {
        form.reset();
        setSelectedAssignee(null);
        onOpenChange(false);
        onSuccess();
      },
    });
  }

  function handleOpenChange(value: boolean) {
    if (!value) {
      form.reset();
      setSelectedAssignee(null);
    }
    onOpenChange(value);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-md" data-testid="tasks-bulk-edit-dialog-root">
        <DialogHeader>
          <DialogTitle>{t('bulkEdit.dialogTitle', { count: taskIds.length })}</DialogTitle>
          <DialogDescription>{t('bulkEdit.hint')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-2">
          {form.formState.errors.root && (
            <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
          )}

          <Controller
            control={form.control}
            name="statusName"
            render={({ field }) => (
              <Field>
                <FieldLabel className={FIELD_LABEL_CLASS}>{t('bulkEdit.status')}</FieldLabel>
                <Select
                  value={field.value || NO_CHANGE}
                  onValueChange={(v) => field.onChange(v === NO_CHANGE ? '' : v)}
                >
                  <SelectTrigger
                    className={FIELD_BOX_CLASS}
                    data-testid="tasks-bulk-edit-dialog-status-select"
                  >
                    <CircleDot className="size-4 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHANGE}>{t('bulkEdit.noChange')}</SelectItem>
                    {statuses.map((s) => (
                      <SelectItem key={s.id} value={s.name}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="groupName"
            render={({ field }) => (
              <Field>
                <FieldLabel className={FIELD_LABEL_CLASS}>{t('bulkEdit.group')}</FieldLabel>
                <Select
                  value={field.value || NO_CHANGE}
                  onValueChange={(v) => field.onChange(v === NO_CHANGE ? '' : v)}
                >
                  <SelectTrigger
                    className={FIELD_BOX_CLASS}
                    data-testid="tasks-bulk-edit-dialog-group-select"
                  >
                    <CircleDot className="size-4 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHANGE}>{t('bulkEdit.noChange')}</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.name}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="priority"
            render={({ field }) => (
              <Field>
                <FieldLabel className={FIELD_LABEL_CLASS}>{t('bulkEdit.priority')}</FieldLabel>
                <Select
                  value={field.value || NO_CHANGE}
                  onValueChange={(v) => field.onChange(v === NO_CHANGE ? '' : v)}
                >
                  <SelectTrigger
                    className={FIELD_BOX_CLASS}
                    data-testid="tasks-bulk-edit-dialog-priority-select"
                  >
                    <Flag className="size-4 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHANGE}>{t('bulkEdit.noChange')}</SelectItem>
                    {priorityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
          />

          <Field>
            <FieldLabel className={FIELD_LABEL_CLASS}>{t('bulkEdit.assignee')}</FieldLabel>
            <div
              className={`flex items-center rounded-lg border border-input bg-transparent px-3.5 py-[9px] dark:bg-input/30 ${clearAssignee ? 'opacity-50' : ''}`}
            >
              <CircleUser className="size-4 shrink-0 text-muted-foreground" />
              <div className="ml-2.5 min-w-0 flex-1">
                <AssigneePicker
                  projectId={projectId}
                  assignee={selectedAssignee}
                  onSelect={handleAssigneeSelect}
                  disabled={!!clearAssignee}
                  placeholder={t('bulkEdit.noChange')}
                />
              </div>
            </div>
          </Field>

          <div className="flex items-center gap-3">
            <Switch
              id="bulk-edit-clear-assignee"
              checked={clearAssignee ?? false}
              onCheckedChange={(checked) => {
                form.setValue('clearAssignee', checked);
                if (checked) {
                  form.setValue('assigneeEmail', '');
                  setSelectedAssignee(null);
                }
              }}
              data-testid="tasks-bulk-edit-dialog-clear-assignee-toggle"
            />
            <Label htmlFor="bulk-edit-clear-assignee" className="cursor-pointer text-sm">
              {t('bulkEdit.clearAssignee')}
            </Label>
          </div>

          <FormFieldDatePicker
            control={form.control}
            name="startDate"
            label={t('bulkEdit.startDate')}
            placeholder={t('bulkEdit.noChange')}
            className={FIELD_BOX_CLASS}
            data-testid="tasks-bulk-edit-dialog-start-date-picker"
          />

          <FormFieldDatePicker
            control={form.control}
            name="dueDate"
            label={t('bulkEdit.dueDate')}
            placeholder={t('bulkEdit.noChange')}
            className={FIELD_BOX_CLASS}
            data-testid="tasks-bulk-edit-dialog-due-date-picker"
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              {t('subtasks.confirmDelete.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              data-testid="tasks-bulk-edit-dialog-submit-btn"
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('bulkEdit.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
